import { cloneDeep } from '@apollo/client/utilities';
import { EntityInput, forCreate, forDelete, forUpdate } from '../common/entity';
import { Order, OrderType } from './order';
import { CustomField, CustomFieldEntityType, CustomFieldValue } from '../custom_field/customField';
import { ProductTransactionSpecifiers } from '../productTransaction';
import { OrderUtil } from './order.util';
import { Product } from '../product';
import { Address } from '../common/address';
import { UnitOfMeasure } from '../uom';

export class OrderInput extends Order implements EntityInput<Order> {
  forCreate(): CreateOrderInput {
    delete (this as any).authorization;
    return {
      ...this,
      customFields: this.customFieldValues(),
      products: [...this.products.values()].map(p => {
        delete p.id;
        return {
          ...p,
          toCustomFields: Array.from(p.toCustomFields.values()),
          fromCustomFields: Array.from(p.fromCustomFields.values()),
        };
      }),
    };
  }

  forUpdate(): UpdateOrderInput {
    delete (this as any).authorization;
    return {
      ...UpdateOrderInput.from(this, UpdateOrderInput),
      products: undefined,
      customFields: this.customFieldValues(),
    } as any;
  }

  forDelete(): DeleteOrderInput {
    delete (this as any).authorization;
    return DeleteOrderInput.from(this, DeleteOrderInput);
  }

  validate(customFields: Map<string, CustomField>) {
    switch (this.type) {
      case OrderType.inbound:
      case OrderType.replenish:
        if (!this.toStockLocationId) return false;
        break;
      case OrderType.outbound:
      case OrderType.move:
      case OrderType.disposal:
        if (!this.fromStockLocationId) return false;
        break;
      case OrderType.move:
        if (!this.fromStockLocationId || !this.toStockLocationId) return false;
        break;
    }

    const mandatoryCustomFields = [...customFields.values()].filter(
      cf =>
        cf.entityType === CustomFieldEntityType.order &&
        (!cf.entitySubtype || cf.entitySubtype.toString() === this.type.toString()) &&
        cf.mandatory,
    );
    if (mandatoryCustomFields.some(cf => !this.customFields.get(cf.id)?.value)) return false;

    return true;
  }

  withFromStockLocationId(stockLocationId: string | undefined) {
    this.fromStockLocationId = stockLocationId;
    this.products = new Map();
    return cloneDeep(this);
  }

  withToStockLocationId(stockLocationId: string | undefined) {
    this.toStockLocationId = stockLocationId;
    this.products = new Map();
    return cloneDeep(this);
  }

  withType(type: OrderType) {
    if (this.type === type) return cloneDeep(this);
    if (type === OrderType.inbound) this.fromStockLocationId = undefined;
    if (type === OrderType.outbound || type === OrderType.disposal) this.toStockLocationId = undefined;
    this.type = type;
    this.products = new Map();
    return cloneDeep(this);
  }

  withContactId(contactId?: string) {
    this.contactId = contactId;
    return cloneDeep(this);
  }

  withAddress(address?: Address) {
    this.address = address;
    return cloneDeep(this);
  }

  withAssignedUsers(ids: string[]) {
    this.assignedTo = ids;
    return cloneDeep(this);
  }

  withProduct(pmdId: string, customFieldValue?: CustomFieldValue, toContainerId?: string) {
    const specifier = new ProductTransactionSpecifiers({
      pmdId: pmdId,
      id: pmdId,
      quantity: 1,
      toStockLocationId: this.toStockLocationId,
      fromStockLocationId: this.fromStockLocationId,
      toContainerId,
      toCustomFields: customFieldValue ? [customFieldValue] : [],
    });
    this.products.set(OrderUtil.orderSpecifierHash(specifier), specifier);
    return cloneDeep(this);
  }

  withOutboundProduct(product: Product, uom: UnitOfMeasure | undefined) {
    const specifier = new ProductTransactionSpecifiers({
      pmdId: product.productMasterDataId,
      id: product.id,
      quantity: parseFloat(UnitOfMeasure.toQuantity(uom, product.unitQuantity)),
      toStockLocationId: undefined,
      fromStockLocationId: product.stockLocationId,
      fromBinId: product.binId,
      toBinId: undefined,
      fromContainerId: product.containerId,
      toContainerId: undefined,
    });

    const hash = OrderUtil.orderSpecifierHash(specifier);
    const existing = this.products.get(hash);
    if (existing) {
      specifier.quantity += existing.quantity;
    }

    this.products.set(OrderUtil.orderSpecifierHash(specifier), specifier);
    return cloneDeep(this);
  }

  withContainer(containerId: string, customFieldValue?: CustomFieldValue) {
    const specifier = new ProductTransactionSpecifiers({
      containerId: containerId,
      id: containerId,
      quantity: 1,
      toStockLocationId: this.toStockLocationId,
      fromStockLocationId: this.fromStockLocationId,
    });
    this.products.set(OrderUtil.orderSpecifierHash(specifier), specifier);
    return cloneDeep(this);
  }

  withSpecifierCustomField(specifierHash: string, customField: CustomFieldValue) {
    const existing = this.products.get(specifierHash);
    if (!existing) return cloneDeep(this);

    this.products.set(specifierHash, existing.withToCustomField(customField));
    return cloneDeep(this);
  }

  removeProducts(hashes: Set<string>) {
    hashes.forEach(hash => this.products.delete(hash));
    return cloneDeep(this);
  }
}

export class CreateOrderInput extends forCreate(OrderInput) {}

export class UpdateOrderInput extends forUpdate(OrderInput) {}

export class DeleteOrderInput extends forDelete(OrderInput) {}
